import { Badge, Group, Paper, Progress, Stack, Text } from "@mantine/core"
import { useTranslation } from "react-i18next"
import type { AgencyUser } from "../models/agencyUser"
import { MAX_2FA_ATTEMPTS } from "../models/agencyUser"
import { EmptyValue } from "../utils"

interface Props {
  user: AgencyUser
}

export default function SecurityTabContent({ user }: Props) {
  const { t } = useTranslation()

  const formatRelativeTime = (dateStr: string | null) => {
    if (!dateStr) return <EmptyValue />
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) return <EmptyValue />
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffSecs = Math.floor(diffMs / 1000)
    const diffMins = Math.floor(diffSecs / 60)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffSecs < 60) return "agora mesmo"
    if (diffMins < 60) return `há ${diffMins}min`
    if (diffHours < 24) return `há ${diffHours}h`
    if (diffDays < 30) return `há ${diffDays} dias`
    return date.toLocaleDateString("pt-PT", {
      day: "numeric",
      month: "long",
      year: "numeric",
    })
  }

  const progressPercent = Math.min(
    (user.failed_2fa_attempts / MAX_2FA_ATTEMPTS) * 100,
    100,
  )
  const progressColor =
    user.failed_2fa_attempts >= MAX_2FA_ATTEMPTS
      ? "red"
      : user.failed_2fa_attempts >= 2
        ? "orange"
        : "green"

  return (
    <Stack gap="md">
      <Group gap="lg" align="flex-start" wrap="wrap">
        {/* 2FA */}
        <Paper withBorder p="md" radius="md" style={{ flex: "1 1 200px", minWidth: 200 }}>
          <Text fw={600} size="sm" mb="sm">
            {t("agencyUsers.twoFA")}
          </Text>
          {user.two_fa_type === "authenticator" ? (
            <>
              <Badge color="green" variant="light" size="lg">
                <i className="fa fa-mobile" />{" "}
                {t("agencyUsers.twoFAAuthenticator")}
              </Badge>
              <Text size="xs" c="dimmed" mt={4}>
                {t("agencyUsers.twoFAAuthenticatorHelp")}
              </Text>
            </>
          ) : (
            <>
              <Badge color="blue" variant="light" size="lg">
                <i className="fa fa-envelope" />{" "}
                {t("agencyUsers.twoFAEmail")}
              </Badge>
              <Text size="xs" c="dimmed" mt={4}>
                {t("agencyUsers.twoFAEmailHelp")}
              </Text>
            </>
          )}
        </Paper>

        {/* Failed 2FA Attempts */}
        <Paper withBorder p="md" radius="md" style={{ flex: "1 1 200px", minWidth: 200 }}>
          <Text fw={600} size="sm" mb="sm">
            {t("agencyUsers.failed2faAttempts")}
          </Text>
          <Group gap="sm" mb="xs">
            <Text size="sm">
              {user.failed_2fa_attempts} / {MAX_2FA_ATTEMPTS}
            </Text>
            {user.failed_2fa_attempts >= MAX_2FA_ATTEMPTS && (
              <Badge color="red" variant="light" size="sm">
                <i className="fa fa-exclamation-triangle" />{" "}
                {t("agencyUsers.accountBlocked")}
              </Badge>
            )}
          </Group>
          <Progress
            value={progressPercent}
            color={progressColor}
            size="md"
            radius="sm"
            animated={user.failed_2fa_attempts >= MAX_2FA_ATTEMPTS}
          />
        </Paper>

        {/* Last Failed Login */}
        <Paper withBorder p="md" radius="md" style={{ flex: "1 1 200px", minWidth: 200 }}>
          <Text fw={600} size="sm" mb="sm">
            {t("agencyUsers.lastFailedLogin")}
          </Text>
          <Text size="sm">
            {user.last_login_attempt_at
              ? formatRelativeTime(user.last_login_attempt_at)
              : t("agencyUsers.never")}
          </Text>
        </Paper>
      </Group>

      {/* Last Login */}
      {user.last_login_at && (
        <Paper withBorder p="md" radius="md">
          <Text fw={600} size="sm" mb="sm">
            {t("agencyUsers.lastLogin")}
          </Text>
          <Group gap="lg" wrap="wrap">
            <Text size="sm">
              <i className="fa fa-clock-o" />{" "}
              {formatRelativeTime(user.last_login_at)}
            </Text>
            <Text size="sm">
              <i className="fa fa-globe" />{" "}
              {user.last_login_ip || <EmptyValue />}
            </Text>
            <Text size="sm">
              <i className="fa fa-laptop" />{" "}
              {user.last_login_browser || ""}
              {user.last_login_os ? ` (${user.last_login_os})` : ""}
            </Text>
          </Group>
        </Paper>
      )}

      {/* Timestamps */}
      <Group gap="md" wrap="wrap">
        <Text size="xs" c="dimmed">
          {t("agencyUsers.registeredAt")}{" "}
          <Text component="span" c="dark">
            {formatRelativeTime(user.created_at)}
          </Text>
        </Text>
        <Text size="xs" c="dimmed">
          {t("agencyUsers.updatedAt")}{" "}
          <Text component="span" c="dark">
            {formatRelativeTime(user.updated_at)}
          </Text>
        </Text>
      </Group>
    </Stack>
  )
}
